import { useState } from 'react'
import { post } from '../../../../../utils/Api'
import { useGlobalContext } from '../../../../../utils/Context'
import FormLoader from '../../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'

interface Props {
  invoiceId: string
  currentDeadline: string
  onClose: () => void
  onSuccess: () => void
}

function ExtendDeadlineModal({
  invoiceId,
  currentDeadline,
  onClose,
  onSuccess,
}: Props) {
  const { notify } = useGlobalContext()
  const [loading, setLoading] = useState(false)
  const [additionalDays, setAdditionalDays] = useState('7')
  const [reason, setReason] = useState('')

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      dateStyle: 'full',
      timeStyle: 'short',
    })
  }

  const calculateNewDeadline = () => {
    const currentDate = currentDeadline
      ? new Date(currentDeadline)
      : new Date()
    const days = parseInt(additionalDays) || 0
    const newDate = new Date(currentDate.getTime() + days * 24 * 60 * 60 * 1000)
    return newDate.toLocaleString('en-NG', {
      dateStyle: 'full',
      timeStyle: 'short',
    })
  }

  const handleExtend = async () => {
    const days = parseInt(additionalDays)
    if (!days || days <= 0) {
      notify('warn', 'Please enter a valid number of days')
      return
    }

    setLoading(true)

    try {
      const response = await post(
        `admin/weight-discrepancy/invoices/${invoiceId}/extend-deadline`,
        {
          invoiceId,
          additionalDays: days,
          reason: reason.trim() || undefined,
        },
        null,
      )

      if (response.status === 200) {
        notify('success', 'Deadline extended successfully')
        onSuccess()
        onClose()
      } else {
        notify('error', response.message || 'Failed to extend deadline')
      }
    } catch (error) {
      notify('error', 'Failed to extend deadline')
    } finally {
      setLoading(false)
    }
  }

  const quickOptions = [3, 7, 14, 30]

  return (
    <div className='modal-overlay'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2>Extend Payment Deadline</h2>
          <button className='close-btn' onClick={onClose}>
            <Icon icon='mdi:close' />
          </button>
        </div>

        <div className='modal-body'>
          <div
            style={{
              textAlign: 'center',
              padding: '20px 0',
            }}
          >
            <Icon
              icon='mdi:calendar-plus'
              style={{
                fontSize: '64px',
                color: '#ff9800',
                marginBottom: '20px',
              }}
            />
            <h3 style={{ marginBottom: '10px' }}>Extend Deadline</h3>
            <p style={{ color: '#666', marginBottom: '10px' }}>
              Give the customer more time to pay their invoice.
            </p>
          </div>

          {currentDeadline && (
            <div
              className='check-result'
              style={{ backgroundColor: '#f5f5f5', marginBottom: '20px' }}
            >
              <div className='result-row'>
                <span>Current Deadline</span>
                <span>{formatDate(currentDeadline)}</span>
              </div>
            </div>
          )}

          <div className='form-group'>
            <label>Additional Days *</label>
            <input
              type='number'
              value={additionalDays}
              onChange={(e) => setAdditionalDays(e.target.value)}
              placeholder='Enter number of days'
              min='1'
            />
            <div
              style={{
                display: 'flex',
                gap: '10px',
                marginTop: '10px',
                flexWrap: 'wrap',
              }}
            >
              {quickOptions.map((days) => (
                <button
                  key={days}
                  type='button'
                  onClick={() => setAdditionalDays(days.toString())}
                  style={{
                    padding: '8px 16px',
                    border:
                      additionalDays === days.toString()
                        ? '2px solid #446ce4'
                        : '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor:
                      additionalDays === days.toString() ? '#e8eaf6' : '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {days} days
                </button>
              ))}
            </div>
          </div>

          {additionalDays && parseInt(additionalDays) > 0 && (
            <div
              className='check-result'
              style={{ backgroundColor: '#e8f5e9', marginBottom: '20px' }}
            >
              <div className='result-row highlight'>
                <span>New Deadline</span>
                <span style={{ color: '#4caf50' }}>{calculateNewDeadline()}</span>
              </div>
            </div>
          )}

          <div className='form-group'>
            <label>Reason (Optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='e.g., Customer requested extension due to temporary cash flow issues'
            />
          </div>

          <div
            style={{
              backgroundColor: '#e3f2fd',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            <p style={{ margin: 0, fontSize: '14px', color: '#1565c0' }}>
              <Icon
                icon='mdi:information'
                style={{ marginRight: '8px', verticalAlign: 'middle' }}
              />
              If the invoice is overdue, extending the deadline will reactivate
              it and unblock the user.
            </p>
          </div>

          <div className='modal-actions'>
            <button className='btn-secondary' onClick={onClose}>
              Cancel
            </button>
            {loading ? (
              <FormLoader position='center' />
            ) : (
              <button className='btn-primary' onClick={handleExtend}>
                <Icon icon='mdi:calendar-plus' style={{ marginRight: '5px' }} />
                Extend Deadline
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExtendDeadlineModal
